import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ClipboardList, Play, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import axios from "../../api/axios";
import { AuthContext } from "../../context/AuthContext";
import {
  masterContainer,
  masterAmbientGradient1,
  masterAmbientGradient2,
  masterScrollArea,
  masterPanelCard,
  masterStickyHeadRow,
  masterTableRow,
  masterEmptyState,
  masterMockFilterBar,
  masterMockTabBtnInactive,
  masterMockBadgeCompleted,
  masterMockBadgePending,
  masterMockBadgeNotAttempted,
  masterMockStartBtn,
} from "../../GlobalComponents/MasterCssClass";

// DD-MM-YYYY Date Formatter
const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return "Flexible";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "Flexible";

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
};

const getLatestMockSchedule = (test = {}) => {
  const historyList = Array.isArray(test.mockTestPublishedHistory) ? test.mockTestPublishedHistory : [];
  const latestPublished = [...historyList]
    .reverse()
    .find((item) => String(item?.action || "").toUpperCase() === "PUBLISHED" && item?.testDate && item?.testTime);

  return {
    testDate: latestPublished?.testDate || test.testDate,
    testTime: latestPublished?.testTime || test.testTime,
    durationMinutes: Math.max(parseInt(latestPublished?.durationMinutes || test.durationMinutes, 10) || 30, 1),
  };
};

const getMockWindowState = (test = {}, currentTime = new Date()) => {
  const schedule = getLatestMockSchedule(test);
  if (!schedule.testDate || !schedule.testTime) {
    return { ...schedule, isVisibleInDirectory: true, isStartEnabled: true, label: "Start Exam" };
  }

  const [hours, minutes] = String(schedule.testTime || "").split(":").map(Number);
  const startAt = new Date(schedule.testDate);
  startAt.setHours(hours || 0, minutes || 0, 0, 0);
  
  const showDirectoryAt = new Date(startAt.getTime() - 1 * 60 * 1000); // 5 mins before start time
  const expireAt = new Date(startAt.getTime() + schedule.durationMinutes * 60 * 1000);

  const isVisibleInDirectory = currentTime >= showDirectoryAt && currentTime <= expireAt;
  const isExpired = currentTime > expireAt;
  const isStartEnabled = currentTime >= startAt && !isExpired;

  let label = "Upcoming";
  if (isExpired) label = "Expired";
  else if (isStartEnabled) label = "Start Now";

  return { ...schedule, isVisibleInDirectory, isExpired, isStartEnabled, label };
};

const StudentMockTestView = () => {
  const { catId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [mockTests, setMockTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTab, setFilterTab] = useState("ALL");
  const [currentTime, setCurrentTime] = useState(new Date());

  const testTypeParam = searchParams.get("type") || "MOCK";

  const isMainAdmin =
    String(user?.email || "").toLowerCase() === "admin@gmail.com" ||
    String(user?.role || "").toLowerCase() === "admin";

  // Live Timer (every 1 second to update button states smoothly)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchCategoryMockTests = async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const res = await axios.get("/public/sidebar-menu");
      if (res.data && res.data.success) {
        const menuData = Array.isArray(res.data.data) ? res.data.data : [];
        
        let foundEntries = [];
        menuData.forEach((exam) => {
          if (!exam.categories) return;
          exam.categories.forEach((cat) => {
            if (String(cat._id) === String(catId) || String(cat.catId) === String(catId)) {
              if (testTypeParam === "PRACTICE") {
                foundEntries = cat.practiceSetEntries || [];
              } else {
                foundEntries = cat.mockTestEntries || [];
              }
            }
          });
        });
        setMockTests(foundEntries);
      }
    } catch (err) {
      console.error("Failed to load test items list", err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  // Initial fetch and automatic background polling every 30 seconds
  useEffect(() => {
    if (catId) {
      fetchCategoryMockTests(false);
      const pollInterval = setInterval(() => {
        fetchCategoryMockTests(true); // background fetch without showing loader
      }, 30000);
      return () => clearInterval(pollInterval);
    }
  }, [catId, testTypeParam]);

  const visibleTests = testTypeParam === "PRACTICE"
    ? mockTests
    : mockTests.filter((test) => getMockWindowState(test, currentTime).isVisibleInDirectory);

  const filteredTests = visibleTests.filter((test) => {
    const status = String(test.attemptStatus || "NOT_ATTEMPTED").toUpperCase();
    if (filterTab === "COMPLETED") return status === "COMPLETED" || status === "SUBMITTED";
    if (filterTab === "PENDING") return status === "IN_PROGRESS" || status === "SAVED";
    if (filterTab === "NOT_ATTEMPTED") return status === "NOT_ATTEMPTED";
    return true;
  });

  const getStatusBadge = (status) => {
    const s = String(status || "").toUpperCase();
    if (s === "COMPLETED" || s === "SUBMITTED") {
      return (
        <span className={`${masterMockBadgeCompleted} !px-2 !py-0.5 !text-[9px]`}>
          <CheckCircle2 size={9} /> Completed
        </span>
      );
    }
    if (s === "IN_PROGRESS" || s === "SAVED") {
      return (
        <span className={`${masterMockBadgePending} !px-2 !py-0.5 !text-[9px]`}>
          <Clock size={9} /> Pending
        </span>
      );
    }
    return (
      <span className={`${masterMockBadgeNotAttempted} !px-2 !py-0.5 !text-[9px]`}>
        <AlertCircle size={9} /> Not Attempted
      </span>
    );
  };

  const handleStartTest = (entry) => {
    const basePath = isMainAdmin
      ? "/dashboard"
      : String(user?.role || "").toLowerCase() === "organization"
      ? "/organization-dashboard"
      : "/student";

    if (testTypeParam === "PRACTICE") {
      navigate(`${basePath}/practice-set-attempt/${entry.subjectId}?questionSetId=${encodeURIComponent(entry.questionSetId)}`);
    } else {
      navigate(`${basePath}/mock-test-attempt/${entry.subjectId}?questionSetId=${encodeURIComponent(entry.questionSetId)}`);
    }
  };

  return (
    <div className={masterContainer}>
      <div className={masterAmbientGradient1} />
      <div className={masterAmbientGradient2} />
      
      <div className={`${masterScrollArea} !gap-2`}>
        <div className={`${masterPanelCard} !p-2.5`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h1 className="text-sm sm:text-base font-extrabold text-slate-900 tracking-tight flex items-center gap-1">
                <ClipboardList className="text-[#0E3652]" size={16} /> 
                {testTypeParam === "PRACTICE" ? "Practice Set Directory" : "Mock Test Directory"}
              </h1>
              <p className="text-[11px] text-slate-500 mt-0.5">Select a test below to start your assessment or review past status.</p>
            </div>

            <div className={`${masterMockFilterBar} !p-0.5`}>
              {[
                { key: "ALL", label: "All", color: "text-slate-800", activeColor: "bg-[#0E3652] text-white" },
                { key: "NOT_ATTEMPTED", label: "Not Attempted", color: "text-slate-600", activeColor: "bg-slate-700 text-white" },
                { key: "PENDING", label: "Pending", color: "text-amber-700 font-bold", activeColor: "bg-amber-600 text-white" },
                { key: "COMPLETED", label: "Completed", color: "text-emerald-600 font-extrabold", activeColor: "bg-emerald-600 text-white" },
              ].map((tab) => {
                const isActiveTab = filterTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setFilterTab(tab.key)}
                    className={`px-2 py-0.5 rounded-lg text-[9px] font-bold transition-all cursor-pointer ${
                      isActiveTab 
                        ? `${tab.activeColor} shadow-xs` 
                        : `${masterMockTabBtnInactive} ${tab.color}`
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-300/80 bg-white p-2 shadow-xs">
          <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className={`${masterStickyHeadRow} text-[11px]`}>
                  <th className="p-1 pl-2.5">#</th>
                  <th className="p-1">Subject Name</th>
                  <th className="p-1">Topic / Subtopic Name</th>
                  <th className="p-1 text-center">Test Date</th>
                  {/* <th className="p-1 text-center">Status</th> */}
                  <th className="p-1 pr-2.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-transparent">
                {loading ? (
                  <tr>
                    <td colSpan="6" className={`${masterEmptyState} py-4 text-xs`}>Loading tests...</td>
                  </tr>
                ) : filteredTests.length > 0 ? (
                  filteredTests.map((entry, idx) => {
                    const windowState = testTypeParam === "PRACTICE"
                      ? { isStartEnabled: true, label: "Start Exam", testDate: entry.testDate, testTime: entry.testTime }
                      : getMockWindowState(entry, currentTime);
                    const isLocked = !windowState.isStartEnabled;

                    return (
                      <tr key={entry._id || entry.questionSetId} className={masterTableRow}>
                        <td className="p-1 pl-2.5 text-[11px] font-bold text-slate-500">{idx + 1}</td>
                        <td className="p-1 text-[11px] font-bold text-slate-800">{entry.subjectName || "---"}</td>
                        <td className="p-1 text-[11px] font-semibold text-slate-700">{entry.displayLabel || entry.questionSetId}</td>
                        <td className="p-1 text-center text-[11px] text-slate-800 font-semibold">{formatDateDDMMYYYY(windowState.testDate)} {windowState.testTime ? `| ${windowState.testTime}` : ""}</td>
                        {/* <td className="p-1 text-center">{getStatusBadge(entry.attemptStatus)}</td> */}
                        <td className="p-1 pr-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => handleStartTest(entry)}
                            disabled={isLocked}
                            className={`${masterMockStartBtn} !px-2.5 !py-0.5 !text-[10px] ${isLocked ? "opacity-50 cursor-not-allowed grayscale bg-slate-400" : ""}`}
                          >
                            <Play size={10} fill="white" /> {windowState.label}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className={`${masterEmptyState} py-4 text-xs`}>
                      No {testTypeParam === "PRACTICE" ? "practice set" : "mock test"} entries found matching this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentMockTestView;